import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Land Rover Defender 90 (classic, short-wheelbase) — an upright, utilitarian
// 4x4 at true scale (1 unit = 1 metre): length ~3.88 (body; the tailgate spare
// adds a little), width 1.79 (about 1.94 over the tyres), height 2.02 (tall),
// wheelbase 2.36, on chunky 0.37-radius off-road tyres. Nose is +Z. Signature
// cues: an extremely BOXY, slab-sided body of flat vertical/horizontal panels;
// a flat, level clamshell bonnet standing PROUD of (higher than) the wings,
// with round headlamps and a mesh grille in the flat vertical front panel below
// its leading edge; a flat steel front bumper; prominent square black plastic
// wheel-arch flares; chunky tyres on WHITE steel wheels with visible bolts; an
// upright flat windscreen; a flat, level WHITE contrasting roof over the green
// body (the iconic white-roof Defender); safari alpine side windows; a
// side-hinged rear door carrying an external SPARE WHEEL with small square tail
// lamps beside it; a black snorkel up the right A-pillar; roof gutters and
// exposed hinges.

const PAINT = "#3d5c3a"; // iconic Coniston Green — the "paint" slot default
const ROOF = "#eceeec"; // classic contrasting white roof (unslotted)
const TRIM = "#17181b"; // gloss-black trim (pillars, hinges, snorkel, brackets)
const CLAD = "#1e2020"; // matte black plastic (sills, aprons)
const FLARE = "#1e1f21"; // matte-black square wheel-arch flares (distinct from the green body)
const WELL = "#141518"; // wheel wells
const GLASS = "#22303e"; // tinted side / rear glazing
const SCREEN = "#31465a"; // windscreen (a shade lighter than the tint)
const CHROME = "#ccd1d6";
const SILVER = "#adb3ba"; // grille mesh slats
const STEEL = "#e6e8e6"; // white steel wheels
const BOLT = "#9aa0a0"; // wheel bolts / hub detail
const STEELBAR = "#5c6060"; // steel bumper bars
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const RED = "#a91527";
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.37; // chunky off-road tyre
const WHEEL_X = 0.8; // half track (tyre outer faces proud of the flares)
const AXLE_F = 1.18;
const AXLE_R = -1.18; // wheelbase 2.36
const BODY_HW = 0.89; // slab body half width (1.78 over the panels)
const NOSE = 1.92; // front body face
const TAIL = -1.92; // rear body face (rear door; spare stands proud of it)

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One chunky off-road wheel on a WHITE steel rim: wide tyre, brake disc, a
 *  bright white steel disc recessed within the tyre outer face, five visible
 *  wheel bolts on a small circle, a chrome centre cap. Every part shares `name`
 *  so the `drive` track spins them as one node. `side` is +1 for the right (+x)
 *  wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, 0.34, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.2, 0.2, 0.03, 16, BRAKE, [x - side * 0.1, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.3, 0.3, 0.16, 18, STEEL, [x + side * 0.085, y, z], [0, 0, Math.PI / 2], { name }), // white steel disc, near-flush with the tyre face
    cyl(0.09, 0.09, 0.05, 12, BOLT, [x + side * 0.17, y, z], [0, 0, Math.PI / 2], { name }), // hub boss
  ];
  for (let k = 0; k < 5; k++) {
    const a = (k * 2 * Math.PI) / 5;
    parts.push(
      cyl(0.025, 0.025, 0.05, 8, BOLT, [x + side * 0.175, y + 0.17 * Math.cos(a), z + 0.17 * Math.sin(a)], [0, 0, Math.PI / 2], { name }),
    );
  }
  parts.push(cyl(0.05, 0.05, 0.05, 12, CHROME, [x + side * 0.185, y, z], [0, 0, Math.PI / 2], { name })); // centre cap
  return parts;
}

/** The opened wheel well (a dark disc proud of the body side) plus the
 *  Defender's square plastic arch flare — a slim horizontal cladding box over
 *  the wheel and two short vertical caps wrapping its ends. No round brow: the
 *  flares are simple rectangular trims standing just proud of the slab side. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.48, 0.48, 0.04, 18, WELL, [side * 0.87, WHEEL_R, z], [0, 0, Math.PI / 2]),
    box(0.11, 0.2, 1.12, FLARE, [side * (BODY_HW + 0.02), 0.82, z]), // squared black flare top, over the wheel
    box(0.11, 0.52, 0.14, FLARE, [side * (BODY_HW + 0.02), 0.52, z + 0.49]), // front flare wall
    box(0.11, 0.52, 0.14, FLARE, [side * (BODY_HW + 0.02), 0.52, z - 0.49]), // rear flare wall
  ];
}

// The six hazard indicators: round front lamps in the flat front panel, round
// side repeaters on the front wings, and square amber tail lamps at the rear.
// Parts and blink tracks are generated from one table so the pivots (each
// part's own centre) can never drift from the parts.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; kind: "front" | "side" | "rear" }> = [
  { name: "indicator-fl", pos: [-0.82, 0.86, 1.95], kind: "front" },
  { name: "indicator-fr", pos: [0.82, 0.86, 1.95], kind: "front" },
  { name: "indicator-ml", pos: [-0.905, 0.86, 0.9], kind: "side" },
  { name: "indicator-mr", pos: [0.905, 0.86, 0.9], kind: "side" },
  { name: "indicator-rl", pos: [-0.7, 0.7, -1.96], kind: "rear" },
  { name: "indicator-rr", pos: [0.7, 0.7, -1.96], kind: "rear" },
];

function indicatorPart(ind: (typeof INDICATORS)[number]): Part {
  switch (ind.kind) {
    case "front":
      return cyl(0.06, 0.06, 0.04, 14, AMBER, ind.pos, [Math.PI / 2, 0, 0], { name: ind.name });
    case "side":
      return cyl(0.038, 0.038, 0.04, 12, AMBER, ind.pos, [0, 0, Math.PI / 2], { name: ind.name });
    case "rear":
      return box(0.14, 0.1, 0.05, AMBER, ind.pos, undefined, { name: ind.name });
  }
}

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.055, 0.055, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.42, 0.16, 3.4, TRIM, [0, 0.42, 0])); // underbody / chassis pan (high clearance)
  P.push(box(1.5, 0.14, 0.3, CLAD, [0, 0.4, 1.9])); // apron under the nose, back to the front wells
  P.push(box(1.5, 0.14, 0.24, CLAD, [0, 0.4, -1.9])); // apron under the tail, back to the rear wells

  // -- Body masses (paint): one slab tub + a raised flat clamshell bonnet -----
  P.push(paint(2 * BODY_HW, 0.74, NOSE - TAIL, [0, 0.81, 0])); // main tub, sill (0.44) to waist (1.18)
  P.push(paint(1.5, 0.08, 1.26, [0, 1.24, 1.25])); // flat clamshell bonnet, proud ABOVE the waist/wings
  for (const s of S) P.push(box(0.06, 0.06, 1.24, TRIM, [s * 0.75, 1.26, 1.25])); // bonnet-edge seam / catch line

  // -- Greenhouse: green cabin band under a flat, level WHITE roof ------------
  // Green cabin band (waist 1.18 to 1.70), slab-flat with the body.
  P.push(paint(2 * BODY_HW, 0.52, 2.52, [0, 1.44, -0.64])); // cabin shell (front face 0.62, rear door face -1.90)
  // White roof: a flat level box on top (1.70 to 2.02), a hair wider for a lip.
  P.push(box(1.8, 0.32, 2.62, ROOF, [0, 1.86, -0.6])); // front face 0.71 caps the windscreen header
  for (const s of S) P.push(box(0.03, 0.06, 2.5, TRIM, [s * 0.905, 1.71, -0.6])); // roof rain gutters, proud of the roof edge
  P.push(box(0.7, 0.05, 0.7, ROOF, [0, 2.045, 0.1])); // low safari roof-vent hint, sunk into the roof

  // Upright flat windscreen with a paint frame (the A-pillars) around it.
  P.push(box(1.46, 0.56, 0.05, SCREEN, [0, 1.44, 0.66], [-0.08, 0, 0]));
  for (const s of S) P.push(paint(0.12, 0.58, 0.09, [s * 0.72, 1.44, 0.655], [-0.08, 0, 0])); // A-pillars
  for (const s of S) P.push(box(0.34, 0.014, 0.03, TRIM, [s * 0.32, 1.2, 0.7], [-0.08, 0, 0])); // wipers

  // Side glass: a long proud band on each slab side, split by B/C pillars, plus
  // a small high safari "alpine" light near the rear roof.
  for (const s of S) {
    P.push(box(0.02, 0.36, 2.02, GLASS, [s * (BODY_HW + 0.006), 1.42, -0.5])); // main side glass row
    P.push(box(0.03, 0.4, 0.06, TRIM, [s * (BODY_HW + 0.012), 1.42, -0.35])); // B-pillar
    P.push(paint(0.05, 0.44, 0.16, [s * (BODY_HW + 0.008), 1.44, -1.5])); // C-pillar (rear body)
    P.push(box(0.02, 0.02, 2.02, CHROME, [s * (BODY_HW + 0.01), 1.2, -0.5])); // belt-line strip
    P.push(box(0.02, 0.14, 0.34, GLASS, [s * 0.905, 1.82, -1.2])); // alpine window, proud of the white roof side
  }

  // -- Front fascia: flat panel with a mesh grille, round lamps, steel bumper --
  P.push(box(0.64, 0.44, 0.03, "#26282a", [0, 0.98, 1.93])); // recessed grille aperture
  for (let i = -2; i <= 2; i++) P.push(box(0.02, 0.4, 0.02, SILVER, [i * 0.13, 0.98, 1.955])); // vertical mesh slats
  for (let j = -1; j <= 1; j++) P.push(box(0.6, 0.02, 0.02, SILVER, [0, 0.98 + j * 0.14, 1.955])); // horizontal mesh slats
  P.push(box(0.16, 0.06, 0.02, SILVER, [0, 1.28, 1.945])); // plain badge bar above the grille (no lettering)
  for (const s of S) {
    P.push(cyl(0.15, 0.15, 0.03, 16, TRIM, [s * 0.62, 0.98, 1.93], [Math.PI / 2, 0, 0])); // headlamp bezel
    P.push(cyl(0.125, 0.125, 0.045, 16, LAMP, [s * 0.62, 0.98, 1.955], [Math.PI / 2, 0, 0])); // round headlamp
    P.push(cyl(0.05, 0.05, 0.02, 12, CHROME, [s * 0.62, 0.98, 1.985], [Math.PI / 2, 0, 0])); // headlamp reflector
  }
  P.push(box(1.5, 0.14, 0.14, STEELBAR, [0, 0.56, 1.95])); // flat steel front bumper, narrower than the track
  P.push(box(0.44, 0.1, 0.02, TRIM, [0, 0.72, 1.955])); // plate recess
  P.push(box(0.38, 0.08, 0.02, PLATE, [0, 0.72, 1.965])); // front number plate

  // -- Rear fascia: side-hinged door, external spare, small square lamps ------
  P.push(box(0.6, 0.36, 0.03, GLASS, [-0.16, 1.42, -1.93])); // rear door window (offset from the spare)
  // Spare wheel bolted to the tailgate (does not spin): faces rearward.
  P.push(box(0.3, 0.34, 0.1, TRIM, [0.14, 0.98, -1.95])); // spare carrier plate on the door
  P.push(cyl(WHEEL_R, WHEEL_R, 0.3, 18, TIRE, [0.14, 0.98, -2.0], [Math.PI / 2, 0, 0])); // spare tyre
  P.push(cyl(0.3, 0.3, 0.14, 18, STEEL, [0.14, 0.98, -2.08], [Math.PI / 2, 0, 0])); // spare white steel disc, near-flush
  for (let k = 0; k < 5; k++) {
    const a = (k * 2 * Math.PI) / 5;
    P.push(cyl(0.025, 0.025, 0.05, 8, BOLT, [0.14 + 0.17 * Math.cos(a), 0.98 + 0.17 * Math.sin(a), -2.16], [Math.PI / 2, 0, 0]));
  }
  P.push(cyl(0.05, 0.05, 0.05, 12, CHROME, [0.14, 0.98, -2.17], [Math.PI / 2, 0, 0])); // spare centre cap
  for (const s of S) {
    P.push(box(0.06, 0.1, 0.06, TRIM, [s === 1 ? 0.87 : -0.87, 1.4, -1.92])); // exposed door hinge (upper)
  }
  P.push(box(0.06, 0.1, 0.06, TRIM, [0.87, 1.0, -1.92])); // exposed door hinge (lower, hinge side)
  for (const s of S) {
    P.push(box(0.14, 0.13, 0.05, RED, [s * 0.7, 0.88, -1.95])); // square red tail lamp (amber indicator below it)
  }
  P.push(box(1.5, 0.14, 0.14, STEELBAR, [0, 0.56, -1.95])); // flat steel rear bumper
  P.push(box(0.44, 0.1, 0.02, TRIM, [-0.4, 0.72, -1.955])); // plate recess beside the spare
  P.push(box(0.38, 0.08, 0.02, PLATE, [-0.4, 0.72, -1.965])); // rear number plate

  // -- Side furniture: sills, handles, mirrors, snorkel ----------------------
  for (const s of S) {
    P.push(box(0.06, 0.1, 1.4, CLAD, [s * 0.9, 0.5, 0])); // sill strip, well to well
    P.push(box(0.03, 0.03, 0.16, CHROME, [s * 0.895, 0.9, 0.35])); // front door handle
    P.push(box(0.03, 0.03, 0.16, CHROME, [s * 0.895, 0.9, -0.6])); // rear door handle
    P.push(box(0.04, 0.14, 0.04, TRIM, [s * 0.895, 1.02, 0.86])); // exposed front-door hinge (upper)
    P.push(box(0.04, 0.14, 0.04, TRIM, [s * 0.895, 0.72, 0.86])); // exposed front-door hinge (lower)
    P.push(box(0.1, 0.03, 0.04, TRIM, [s * 0.9, 1.3, 0.55])); // mirror arm
    P.push(paint(0.08, 0.14, 0.12, [s * 0.98, 1.3, 0.5])); // mirror head
    P.push(box(0.06, 0.11, 0.012, CHROME, [s * 0.985, 1.3, 0.44])); // mirror glass
  }
  // Snorkel: a bold black tube up the right A-pillar/wing, mounted to the body,
  // with a ram-intake head that stays clearly below the roofline.
  P.push(cyl(0.055, 0.055, 0.78, 12, TRIM, [0.91, 1.41, 0.82])); // bold vertical snorkel tube, y 1.02..1.80
  P.push(box(0.13, 0.16, 0.15, TRIM, [0.905, 1.78, 0.82])); // ram-intake head, top at 1.86 (below the roof)
  P.push(box(0.08, 0.05, 0.08, TRIM, [0.9, 1.2, 0.82])); // mount bracket clamping the tube to the wing/A-pillar

  for (const ind of INDICATORS) P.push(indicatorPart(ind));

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const landRoverDefender = defineModel({
  id: "land-rover-defender",
  name: "Land Rover Defender",
  batchId: "cars",
  tags: ["car", "land-rover", "defender", "suv", "offroad", "classic"],
  summary: "A classic Land Rover Defender 90 at true scale, in Coniston Green with a white roof.",
  description:
    "A classic Land Rover Defender 90 (short-wheelbase) at real 1 unit = 1 " +
    "metre scale (about 3.88 long, 1.79 wide, 2.02 tall, 2.36 wheelbase, chunky " +
    "0.37-radius off-road tyres). All the Defender cues: an extremely boxy, " +
    "slab-sided body of flat panels; a raised flat clamshell bonnet above the " +
    "wings; round headlamps and a mesh grille in the flat front panel over a " +
    "steel bumper; square black plastic wheel-arch flares; white steel wheels " +
    "with visible bolts; an upright windscreen; a flat, level WHITE contrasting " +
    "roof over the green body with safari alpine side windows; a side-hinged " +
    "rear door carrying an external spare wheel with square tail lamps; a black " +
    "snorkel up the right A-pillar; roof gutters and exposed hinges. Body panels " +
    "carry the 'paint' recolour slot (Coniston Green); `drive` spins the four " +
    "road wheels (the spare stays still) and `hazards` blinks the round front " +
    "lamps, wing repeaters and rear ambers.",
  author: "Fable 5",
  footprint: 2.1,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
